/**
 * کدام عکسِ رخداد/بیزینس نشان داده شود، و اگر لود نشد بعدی چیست (WHOSUP-302).
 *
 * دو قاعده، هر دو برای هر دو اپ:
 *   1. آدرسی که در `invalid_images` است هرگز نشان داده نمی‌شود — نه عکسِ اصلی
 *      (`image_invalid`، که تریگرِ 0135 از همان جدول پر می‌کند) و نه عکس‌های
 *      اضافه‌ی `metadata.images` (WHOSUP-240)، که پرچمِ `image_invalid` اصلاً
 *      به آن‌ها نمی‌رسد.
 *   2. عکسی که روی دستگاه لود نشد جایش را به آدرسِ بعدی می‌دهد و آخرِ فهرست
 *      یعنی تصویرِ جایگزین (null) — نه یک قابِ شکسته.
 *
 * ⚠ جدولِ `invalid_images` برای کاربرِ عادی خواندنی نیست (RLS بی‌policy)، پس
 * در اپ‌ها `invalid` معمولاً خالی است و قاعده‌ی ۱ برای عکسِ اصلی از راهِ
 * `image_invalid` اجرا می‌شود. عکس‌های اضافه هنوز در هیچ اپی نمایش داده
 * نمی‌شوند؛ هر نمایشی که بعداً اضافه شود باید از `eventImageUrls` بگذرد و
 * فهرستِ غیرمعتبرها را از سرور بگیرد.
 *
 * بدونِ import، تا با `npm test` آزموده شود.
 */

export interface EventImageInput {
  image_url?: string | null;
  image_invalid?: boolean | null;
  /** همان `events.metadata` — فقط کلیدِ `images` خوانده می‌شود. */
  metadata?: unknown;
}

const usable = (u: unknown): u is string =>
  typeof u === 'string' && u.trim() !== '';

/**
 * عکسِ اصلی، اگر نشان‌دادنی است. عمداً به عکس‌های اضافه برنمی‌گردد: وقتی
 * اصلی غیرمعتبر است، اضافه‌ها را بدونِ فهرستِ غیرمعتبرها نمی‌شود تایید کرد.
 */
export function primaryImageUrl(
  e: EventImageInput, invalid: ReadonlySet<string> = new Set(),
): string | null {
  if (e.image_invalid || !usable(e.image_url)) return null;
  const u = e.image_url.trim();
  return invalid.has(u) ? null : u;
}

/** همه‌ی عکس‌های نشان‌دادنی، اصلی اول، بدونِ تکرار و بدونِ غیرمعتبرها. */
export function eventImageUrls(
  e: EventImageInput, invalid: ReadonlySet<string> = new Set(),
): string[] {
  const out: string[] = [];
  const main = primaryImageUrl(e, invalid);
  if (main) out.push(main);
  const raw = (e.metadata as { images?: unknown } | null | undefined)?.images;
  if (Array.isArray(raw)) {
    for (const it of raw) {
      const u = (it as { url?: unknown } | null)?.url;
      if (!usable(u)) continue;
      const t = u.trim();
      // عکسِ اصلی که غیرمعتبر علامت خورده، از راهِ فهرست برنمی‌گردد.
      if (e.image_invalid && t === e.image_url?.trim()) continue;
      if (invalid.has(t) || out.includes(t)) continue;
      out.push(t);
    }
  }
  return out;
}

/** آدرس‌های قابلِ امتحان به ترتیب، بدونِ خالی و تکرار. */
export function imageSources(...urls: (string | null | undefined)[]): string[] {
  return [...new Set(urls.filter(usable))];
}

/**
 * بعد از شکستِ `failed`، چه نشان داده شود؛ null یعنی تصویرِ جایگزین.
 * آدرسی که در فهرست نیست هم یعنی جایگزین — حلقه‌ی بی‌پایان ممکن نیست.
 */
export function nextImageSource(sources: readonly string[], failed: string | null): string | null {
  if (failed == null) return null;
  const i = sources.indexOf(failed);
  return i < 0 ? null : sources[i + 1] ?? null;
}
